import * as React from "react"
import {
  createFormHook,
  createFormHookContexts,
  useStore,
} from "@tanstack/react-form"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  AlertCircleIcon,
  CheckmarkCircle02Icon,
} from "@hugeicons-pro/core-stroke-rounded"

import { groupDigits } from "@car/ui/lib/form-rules"
import { cn } from "@car/ui/lib/utils"
import { Button } from "@car/ui/components/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@car/ui/components/field"
import { Input } from "@car/ui/components/input"
import {
  NativeSelect,
  NativeSelectOption,
} from "@car/ui/components/native-select"
import { Spinner } from "@car/ui/components/spinner"
import { Textarea } from "@car/ui/components/textarea"

// ---------------------------------------------------------------------------
// کیت فرم مشترک روی TanStack Form. قرارداد:
// - اعتبارسنجی فقط onBlur: `validators: { onBlur: schema }` روی useAppForm (ارسال هم همان را اجرا می‌کند).
// - هر فیلد پس از blur (یا اولین تلاش ارسال) وضعیتش را نشان می‌دهد:
//   نامعتبر = حاشیه و آیکن قرمز + پیام، معتبر و پر = حاشیه و آیکن سبز، خالیِ اختیاری = بی‌رنگ.
// - کنترل سفارشی (تقویم، آپلود، دکمه‌های انتخابی) داخل <FormField> با useFieldControl ساخته می‌شود
//   و وقتی کاربر کارش با کنترل تمام شد field.handleBlur() را صدا می‌زند.
// ---------------------------------------------------------------------------

const { fieldContext, formContext, useFieldContext, useFormContext } =
  createFormHookContexts()

type FieldStatus = "idle" | "valid" | "invalid"

type FieldMessage = { message: string }

const isEmptyValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0)

/** خطاهای استاندارد (زاد) شیء {message} هستند و اعتبارسنج تابعی رشته برمی‌گرداند */
const toMessages = (errors: readonly unknown[]): FieldMessage[] =>
  errors.flatMap((error) => {
    if (typeof error === "string") return error ? [{ message: error }] : []
    if (error && typeof error === "object" && "message" in error) {
      const message = String(error.message)
      return message ? [{ message }] : []
    }
    return []
  })

/** وضعیت نمایشی فیلد جاری: فقط بعد از blur یا اولین تلاش ارسال رنگ می‌گیرد */
function useFieldStatus() {
  const field = useFieldContext<unknown>()
  const submissionAttempts = useStore(
    field.form.store,
    (state) => state.submissionAttempts
  )
  const meta = field.state.meta
  const errors = toMessages(meta.errors)
  const shown = meta.isBlurred || submissionAttempts > 0
  const status: FieldStatus = !shown
    ? "idle"
    : errors.length > 0
      ? "invalid"
      : isEmptyValue(field.state.value)
        ? "idle"
        : "valid"
  return { field, status, errors: status === "invalid" ? errors : [] }
}

/** شناسه DOM کنترل یک فیلد؛ برای فوکوس برنامه‌ای از بیرون فرم */
const fieldDomId = (formId: string, name: string) =>
  `${formId}-${name}`.replace(/[^\w-]/g, "-")

/** شناسه، aria و وضعیت برای ساخت کنترل سفارشی داخل <FormField> */
function useFieldControl() {
  const { field, status, errors } = useFieldStatus()
  const id = fieldDomId(field.form.formId, field.name)
  return {
    field,
    status,
    errors,
    id,
    controlProps: {
      id,
      name: field.name,
      onBlur: field.handleBlur,
      "aria-invalid": status === "invalid" ? true : undefined,
      "aria-describedby": `${id}-description ${id}-error`,
    },
  }
}

/** کلاس حاشیه برای کنترل‌های استاندارد؛ حالت نامعتبر را خود کنترل با aria-invalid می‌کشد */
const statusControlClass = (status: FieldStatus) =>
  status === "valid"
    ? "border-success focus-visible:border-success focus-visible:ring-success/30"
    : undefined

function FieldStatusIcon({
  status,
  className,
}: {
  status: FieldStatus
  className?: string
}) {
  if (status === "idle") return null
  return (
    <HugeiconsIcon
      icon={status === "valid" ? CheckmarkCircle02Icon : AlertCircleIcon}
      strokeWidth={2}
      aria-hidden="true"
      data-slot="field-status-icon"
      data-status={status}
      className={cn(
        "pointer-events-none size-4 shrink-0",
        status === "valid" ? "text-success" : "text-destructive",
        className
      )}
    />
  )
}

type FormFieldProps = {
  label: React.ReactNode
  description?: React.ReactNode
  className?: string
  /**
   * آیکن وضعیت کنار برچسب؛ فیلدهای متنی آیکن را داخل کنترل دارند و این را false می‌کنند.
   * برای کنترل سفارشی پیش‌فرض true است.
   */
  labelIcon?: boolean
  /** یا تابعی که شناسه/aria و وضعیت را برای کنترل سفارشی می‌گیرد */
  children:
    | React.ReactNode
    | ((control: ReturnType<typeof useFieldControl>) => React.ReactNode)
}

/** پوسته مشترک: برچسب، کنترل، توضیح، پیام خطا و وضعیت سبز/قرمز */
function FormField({
  label,
  description,
  className,
  labelIcon = true,
  children,
}: FormFieldProps) {
  const control = useFieldControl()
  const { status, errors, id } = control
  return (
    <Field
      data-invalid={status === "invalid" ? true : undefined}
      data-valid={status === "valid" ? true : undefined}
      className={cn("gap-1.5", className)}
    >
      <div className="flex items-center justify-between gap-2">
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        {labelIcon ? <FieldStatusIcon status={status} /> : null}
      </div>
      {typeof children === "function" ? children(control) : children}
      {description ? (
        <FieldDescription id={`${id}-description`}>
          {description}
        </FieldDescription>
      ) : null}
      <FieldError id={`${id}-error`} errors={errors} />
    </Field>
  )
}

type TextFieldProps = Omit<
  React.ComponentProps<"input">,
  "value" | "onChange" | "onBlur" | "id" | "name" | "children"
> & {
  label: React.ReactNode
  description?: React.ReactNode
  fieldClassName?: string
  /** مبلغ: هنگام تایپ سه‌رقم‌سه‌رقم جدا می‌شود («۱٬۲۵۰٬۰۰۰»)؛ parseIntText همین را می‌خواند */
  grouped?: boolean
}

/** ورودی یک‌خطی؛ مقدار فیلد همیشه رشته است (تبدیل عدد و ارقام فارسی در schema/ارسال) */
function TextField({
  label,
  description,
  fieldClassName,
  className,
  grouped,
  ...inputProps
}: TextFieldProps) {
  const { field, status, controlProps } = useFieldControl()
  return (
    <FormField
      label={label}
      description={description}
      className={fieldClassName}
      labelIcon={false}
    >
      {/* dir روی پوسته هم می‌نشیند تا pe-9 ورودی و جای آیکن (end) در یک سمت باشند */}
      <div className="relative" dir={inputProps.dir}>
        <Input
          {...inputProps}
          {...controlProps}
          value={String(field.state.value ?? "")}
          onChange={(event) =>
            field.handleChange(
              grouped ? groupDigits(event.target.value) : event.target.value
            )
          }
          className={cn(
            status !== "idle" && "pe-9",
            statusControlClass(status),
            className
          )}
        />
        <FieldStatusIcon
          status={status}
          className="absolute end-2.5 top-1/2 -translate-y-1/2"
        />
      </div>
    </FormField>
  )
}

type TextareaFieldProps = Omit<
  React.ComponentProps<"textarea">,
  "value" | "onChange" | "onBlur" | "id" | "name" | "children"
> & {
  label: React.ReactNode
  description?: React.ReactNode
  fieldClassName?: string
}

function TextareaField({
  label,
  description,
  fieldClassName,
  className,
  ...textareaProps
}: TextareaFieldProps) {
  const { field, status, controlProps } = useFieldControl()
  return (
    <FormField
      label={label}
      description={description}
      className={fieldClassName}
      labelIcon={false}
    >
      <div className="relative" dir={textareaProps.dir}>
        <Textarea
          {...textareaProps}
          {...controlProps}
          value={String(field.state.value ?? "")}
          onChange={(event) => field.handleChange(event.target.value)}
          className={cn(
            status !== "idle" && "pe-9",
            statusControlClass(status),
            className
          )}
        />
        <FieldStatusIcon status={status} className="absolute end-2.5 top-2.5" />
      </div>
    </FormField>
  )
}

type SelectOption = { value: string; label: React.ReactNode; disabled?: boolean }

type SelectFieldProps = Omit<
  React.ComponentProps<typeof NativeSelect>,
  "value" | "onChange" | "onBlur" | "id" | "name" | "children"
> & {
  label: React.ReactNode
  description?: React.ReactNode
  fieldClassName?: string
  options: readonly SelectOption[]
  /** گزینه خالی اول، مثلاً «انتخاب کنید» */
  placeholder?: string
}

/** فهرست انتخابی بومی؛ آیکن وضعیت کنار برچسب (فلش فهرست جای داخل کنترل را گرفته) */
function SelectField({
  label,
  description,
  fieldClassName,
  options,
  placeholder,
  className,
  ...selectProps
}: SelectFieldProps) {
  const { field, status, controlProps } = useFieldControl()
  const value = String(field.state.value ?? "")
  // a value no option holds (empty at first): the browser would show the first option while
  // the form still holds nothing, so an explicit «انتخاب کنید» stands in until a choice
  const unmatched = !options.some((option) => option.value === value)
  return (
    <FormField
      label={label}
      description={description}
      className={fieldClassName}
    >
      <NativeSelect
        {...selectProps}
        {...controlProps}
        value={value}
        onChange={(event) => field.handleChange(event.target.value)}
        className={cn(
          "w-full",
          status === "valid" &&
            "[&_select]:border-success [&_select]:focus-visible:border-success",
          className
        )}
      >
        {placeholder !== undefined ? (
          <NativeSelectOption value="">{placeholder}</NativeSelectOption>
        ) : unmatched ? (
          <NativeSelectOption value="" disabled>
            انتخاب کنید
          </NativeSelectOption>
        ) : null}
        {options.map((option) => (
          <NativeSelectOption
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </FormField>
  )
}

/** <form> با noValidate که ارسال را به TanStack می‌دهد؛ داخل form.AppForm */
function Form({
  className,
  ...props
}: Omit<React.ComponentProps<"form">, "onSubmit" | "noValidate">) {
  const form = useFormContext()
  return (
    <form
      noValidate
      className={className}
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        const element = event.currentTarget
        // خطای onBlurِ کهنه (فیلد خالی blur شد، بعد پر شد و Enter بی blur) canSubmit را می‌بندد و
        // TanStack اولین ارسال را بی‌صدا رد می‌کند؛ پس اعتبارسنجی blur را پیش از ارسال تازه کن
        void Promise.all([form.validateAllFields("blur"), form.validate("blur")])
          .then(() => form.handleSubmit())
          .then(() => {
            if (form.state.isValid) return
            // پس از رندر وضعیت قرمز، اولین فیلد نادرست را در دید و فوکوس بیاور
            requestAnimationFrame(() => {
              const invalid = element.querySelector<HTMLElement>(
                '[aria-invalid="true"], [data-invalid] :is(input, select, textarea, button)'
              )
              const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches
              invalid?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" })
              invalid?.focus({ preventScroll: true })
            })
          })
      }}
      {...props}
    />
  )
}

type SubmitButtonProps = Omit<React.ComponentProps<typeof Button>, "type"> & {
  /** نشانگر بارگذاری جدا از ارسال فرم (مثلاً mutation بیرونی) */
  pending?: boolean
}

/** دکمه ارسال: همیشه فعال تا کاربر خطاها را ببیند؛ فقط حین ارسال غیرفعال با اسپینر */
function SubmitButton({
  children,
  disabled,
  pending,
  onMouseDown,
  ...props
}: SubmitButtonProps) {
  const form = useFormContext()
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const busy = isSubmitting || Boolean(pending)
  return (
    <Button
      type="submit"
      disabled={busy || disabled}
      onMouseDown={(event) => {
        // فشار موس فیلد فعال را blur می‌کند؛ خطای آن فیلد (که حالا درست شده) برمی‌خیزد، چیدمان جابه‌جا
        // می‌شود و دکمه از زیر موس می‌رود، پس اولین کلیک گم می‌شد. فوکوس را نگه می‌داریم؛ ارسال خودش
        // همه‌ی فیلدها را اعتبارسنجی می‌کند (Form).
        event.preventDefault()
        onMouseDown?.(event)
      }}
      {...props}
    >
      {busy ? <Spinner className="size-4" /> : null}
      {children}
    </Button>
  )
}

/** خطای سطح فرم (پاسخ سرور)؛ خالی = هیچ */
function FormAlert({
  message,
  className,
}: {
  message?: string | null
  className?: string
}) {
  if (!message) return null
  return (
    <p role="alert" className={cn("text-sm text-destructive", className)}>
      {message}
    </p>
  )
}

const { useAppForm, withForm, withFieldGroup } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: { TextField, TextareaField, SelectField, FormField },
  formComponents: { Form, SubmitButton },
})

export {
  useAppForm,
  withForm,
  withFieldGroup,
  useFieldContext,
  useFormContext,
  useFieldControl,
  useFieldStatus,
  fieldDomId,
  useStore,
  FormField,
  TextField,
  TextareaField,
  SelectField,
  FieldStatusIcon,
  FormAlert,
  statusControlClass,
  type FieldStatus,
  type SelectOption,
}
